"use client"
import * as React from "react"

// FIN-867/F6: "any page or feature can contribute extra context... through one shared
// mechanism". A page calls useFeedbackContext({ invoiceId }) once; FeedbackDialog reads the
// merged result only at submit time (via useFeedbackContextApi), so a page's own re-renders
// never trigger a submission or a dialog re-render.

type FeedbackContextRecord = Record<string, unknown>

export interface FeedbackContextApi {
  getContext: () => FeedbackContextRecord
}

interface FeedbackContextRegistry extends FeedbackContextApi {
  register: (id: string, value: FeedbackContextRecord) => void
  unregister: (id: string) => void
}

const FeedbackReactContext = React.createContext<FeedbackContextRegistry | null>(null)

export function FeedbackContextProvider({ children }: { children: React.ReactNode }) {
  const registryRef = React.useRef<Map<string, FeedbackContextRecord>>(new Map())

  const api = React.useMemo<FeedbackContextRegistry>(() => ({
    register: (id, value) => {
      registryRef.current.set(id, value)
    },
    unregister: (id) => {
      registryRef.current.delete(id)
    },
    getContext: () => {
      const merged: FeedbackContextRecord = {}
      for (const value of registryRef.current.values()) {
        Object.assign(merged, value)
      }
      return merged
    },
  }), [])

  return <FeedbackReactContext.Provider value={api}>{children}</FeedbackReactContext.Provider>
}

/** Read-only handle used by FeedbackDialog to merge every currently-registered page's context
 * at submit time. Returns null outside a FeedbackContextProvider (EC10: a page that forgot to
 * register anything, or a tree with no provider mounted, contributes nothing — never throws). */
export function useFeedbackContextApi(): FeedbackContextApi | null {
  return React.useContext(FeedbackReactContext)
}

let nextRegistrationId = 0

/**
 * Registers page-contributed context (F6) for the lifetime of the calling component. `extra`
 * can be a fresh object literal on every render — it's re-registered on every render (cheap: a
 * Map.set of a handful of keys), never on some slower changed-values comparison that risks
 * missing an update.
 */
export function useFeedbackContext(extra: FeedbackContextRecord): void {
  const registry = React.useContext(FeedbackReactContext)
  const idRef = React.useRef<string | undefined>(undefined)
  if (idRef.current === undefined) {
    idRef.current = `feedback-context-${++nextRegistrationId}`
  }

  React.useEffect(() => {
    if (!registry) return
    const id = idRef.current as string
    registry.register(id, extra)
    return () => registry.unregister(id)
  })
}
